import { useState } from 'react'
import clsx from 'clsx'

import { FileCategory } from '@makyoapp/makyo-sdk/models/Files'
import { Image16 } from '@makyoapp/makyo-icon/icon'
import { Loading } from '@makyoapp/makyo-ui'

import { useGetStorageMetric } from '@/resolvers/org/storage/query'
import { useStorageContext } from '@/context/StorageContext'
import { formatBytesToSize } from '@/shared/utils'

import { ReactComponent as OtherIcon } from '@/assets/briefcase.svg?react'
import { ReactComponent as DocumentIcon } from '@/assets/document.svg?react'
import { ReactComponent as AudioIcon } from '@/assets/music.svg?react'

/**
 * New Storage Stats Panel (right) that replaced previous categorization (top)
 */
export const StorageStats = () => {
  const { data: storageMetric, isLoading } = useGetStorageMetric()
  const { activeCategory, setActiveCategory } = useStorageContext() // Accessing activeCategory from context
  const [activeFilter, setActiveFilterState] = useState<FileCategory | null>(null)

  const totalUsed = storageMetric?.used || 0
  const totalSize = storageMetric?.total || 0

  const categories = [
    {
      type: FileCategory.Image,
      name: 'Images',
      icon: Image16,
      files: storageMetric?.categories[FileCategory.Image] || 0,
      size: storageMetric?.size.image || 0,
      color: 'text-purple-400',
      shadowColor: 'drop-shadow-[0_0_4px_rgba(168,85,247,0.9)]',
      bgColor: 'bg-purple-950',
    },
    {
      type: FileCategory.Audio,
      name: 'Audio',
      icon: AudioIcon,
      files: storageMetric?.categories[FileCategory.Audio] || 0,
      size: storageMetric?.size.audio || 0,
      color: 'text-blue-400',
      shadowColor: 'drop-shadow-[0_0_4px_rgba(96,165,250,0.9)]',
      bgColor: 'bg-blue-950',
    },
    {
      type: FileCategory.Document,
      name: 'Documents',
      icon: DocumentIcon,
      files: storageMetric?.categories[FileCategory.Document] || 0,
      size: storageMetric?.size.document || 0,
      color: 'text-amber-400',
      shadowColor: 'drop-shadow-[0_0_4px_rgba(251,191,36,0.9)]',
      bgColor: 'bg-amber-950',
    },
    {
      type: FileCategory.Other,
      name: 'Others',
      icon: OtherIcon,
      files: storageMetric?.categories[FileCategory.Other] || 0,
      size: storageMetric?.size.other || 0,
      color: 'text-teal-400',
      shadowColor: 'drop-shadow-[0_0_4px_rgba(45,212,191,0.9)]',
      bgColor: 'bg-teal-950',
    },
  ]

  const clearFilter = () => {
    setActiveFilterState(null)
  }

  return (
    <aside className='w-full lg:w-80 shrink-0'>
      {isLoading ? (
        <div className='rounded-lg mk-bg p-6 min-h-[400px] flex justify-center items-center'>
          <Loading fullHeight={false} />
        </div>
      ) : (
        <div className='sticky top-0 rounded-sm border bdr bg-white dark:bg-slate-800 p-6'>
          <h2 className='mb-6 text-lg font-semibold '>Storage Usage</h2>

          <div className='mb-6'>
            <div className='mb-2 flex justify-between text-sm'>
              <span className='text-light'>{formatBytesToSize(totalUsed)} used</span>
              <span className=''>{formatBytesToSize(totalSize)}</span>
            </div>
            <div className='h-1 overflow-hidden rounded-full bg-slate-300 dark:bg-slate-700'>
              <div
                className='h-full rounded-full bg-emerald-500'
                style={{ width: `${(totalUsed / totalSize) * 100}%` }}
              />
            </div>
          </div>

          <div className='space-y-4 -mx-3'>
            {categories.map((category) => (
              <button
                key={category.type}
                className={clsx(
                  'w-full flex items-center justify-between rounded-lg p-2 bg-white dark:bg-slate-800 hover:bg-zinc-100 dark:hover:bg-slate-700',
                  activeCategory === category.name ? 'bg-zinc-50 dark:bg-slate-700' : ''
                )}
                onClick={() => setActiveCategory(category.type)}
              >
                <div className='flex items-center gap-1.5'>
                  <div className={`flex items-center justify-center ${category.shadowColor} rounded-full p-2`}>
                    <category.icon className={`${category.color}`} />
                  </div>
                  <span className=''>{category.name}</span>
                </div>

                <div className='text-right'>
                  <div className='text-light'>{category.files} files</div>
                  <div className='font-medium'>{formatBytesToSize(category.size)}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {activeFilter && (
        <div className='mt-4'>
          <span className='text-slate-400'>Showing {categories.find((ft) => ft.type === activeFilter)?.name}</span>
          <button onClick={clearFilter} className='text-blue-500 ml-2'>
            Clear filter
          </button>
        </div>
      )}
    </aside>
  )
}
